import { useState, useEffect } from 'react';
import { Animated } from 'react-native';
import { BUBBLE_FLOAT_TIMING } from '../data/landingData';

const BUBBLE_COUNT = BUBBLE_FLOAT_TIMING.length;

export type BubbleAnimations = {
  floatAnims: Animated.Value[];
  popAnims: Animated.Value[];
  splatAnims: Animated.Value[];
  poppedBubbles: Set<number>;
  popBubble: (index: number) => void;
};

export const useBubbleAnimations = (): BubbleAnimations => {
  const [floatAnims] = useState<Animated.Value[]>(() =>
    Array.from({ length: BUBBLE_COUNT }, () => new Animated.Value(0))
  );

  const [popAnims] = useState<Animated.Value[]>(() =>
    Array.from({ length: BUBBLE_COUNT }, () => new Animated.Value(0))
  );

  const [splatAnims] = useState<Animated.Value[]>(() =>
    Array.from({ length: BUBBLE_COUNT }, () => new Animated.Value(0))
  );

  const [poppedBubbles, setPoppedBubbles] = useState<Set<number>>(new Set());

  useEffect(() => {
    const animations = floatAnims.map((anim, i) => {
      const { delay, duration } = BUBBLE_FLOAT_TIMING[i];
      return Animated.loop(
        Animated.sequence([
          Animated.delay(delay),
          Animated.timing(anim, {
            toValue: 1,
            duration,
            useNativeDriver: true,
          }),
          Animated.timing(anim, {
            toValue: 0,
            duration,
            useNativeDriver: true,
          }),
        ])
      );
    });

    animations.forEach((a) => a.start());
    return () => animations.forEach((a) => a.stop());
  }, [floatAnims]);

  const popBubble = (index: number) => {
    if (poppedBubbles.has(index)) return;

    setPoppedBubbles((prev) => new Set([...prev, index]));

    // Splat particles fly out, then instant-reset so they disappear cleanly
    Animated.sequence([
      Animated.timing(splatAnims[index], {
        toValue: 1,
        duration: 360,
        useNativeDriver: true,
      }),
      Animated.timing(splatAnims[index], {
        toValue: 0,
        duration: 1,
        useNativeDriver: true,
      }),
    ]).start();

    // Burst ring expands, holds, then bubble respawns
    Animated.sequence([
      Animated.timing(popAnims[index], {
        toValue: 1,
        duration: 220,
        useNativeDriver: true,
      }),
      Animated.delay(650),
      Animated.timing(popAnims[index], {
        toValue: 0,
        duration: 380,
        useNativeDriver: true,
      }),
    ]).start(() => {
      setPoppedBubbles((prev) => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });
    });
  };

  return { floatAnims, popAnims, splatAnims, poppedBubbles, popBubble };
};
